import Tracker from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import { OPENREPLAY_URL } from 'utils/axios-request';

/**
 * Servicio de OpenReplay para levita-firebase/hosting.
 *
 * Portado desde levita-front con una diferencia clave: este proyecto NO usa
 * GraphQL/Apollo, sino auth REST (/login). Los datos del usuario quedan en
 * localStorage('user') y en redux (auth.user), así que la identificación se
 * obtiene de ahí en vez de un query `me`.
 *
 * Se activa únicamente en producción (NODE_ENV === 'production').
 */
class OpenReplayService {
  constructor() {
    this.tracker = null;
    this.isProduction = process.env.NODE_ENV === 'production';
    this.isInitializing = false;
    this.isInitialized = false;
    this.isStarted = false;
    this.sessionID = null;
    this.maxRetries = 3;
    this.retryDelay = 2000; // 2 segundos
  }

  /**
   * Lee el usuario autenticado desde localStorage.
   * Devuelve null si no hay sesión válida.
   */
  getStoredUser() {
    try {
      const raw = localStorage.getItem('user');
      if (!raw || raw === 'null' || raw === 'undefined') {
        return null;
      }
      return JSON.parse(raw);
    } catch (error) {
      console.error('[OpenReplay] ❌ Error parsing stored user', error);
      return null;
    }
  }

  /**
   * Valida que existan las dependencias mínimas para identificar al usuario.
   */
  validateDependencies() {
    const validations = [];

    const user = this.getStoredUser();
    if (!user) {
      validations.push('No authenticated user found in localStorage');
    } else if (!user.email && !user.username && !user.id && !user._id) {
      validations.push(
        'Stored user has no identifiable field (email/username/id)'
      );
    }

    if (validations.length > 0) {
      console.error(
        '[OpenReplay] ❌ Dependency validation failed:',
        validations
      );
      return false;
    }

    console.log('[OpenReplay] ✅ All dependencies validated successfully');
    return true;
  }

  /**
   * Inicializa el tracker de OpenReplay
   */
  init() {
    console.log('[OpenReplay] Initializing...', {
      isProduction: this.isProduction,
      nodeEnv: process.env.NODE_ENV,
      isInitialized: this.isInitialized,
      isInitializing: this.isInitializing,
    });

    // Prevenir múltiples inicializaciones
    if (this.isInitialized) {
      console.warn('[OpenReplay] ⚠️ Already initialized, skipping...');
      return true;
    }

    if (this.isInitializing) {
      console.warn(
        '[OpenReplay] ⚠️ Initialization already in progress, skipping...'
      );
      return false;
    }

    // Solo inicializar en producción
    if (!this.isProduction) {
      console.warn(
        '[OpenReplay] ❌ Not initializing - Reason: Not in production environment (NODE_ENV !== "production")'
      );
      return false;
    }

    this.isInitializing = true;

    try {
      // Inicializar el tracker con configuración hardcodeada
      this.tracker = new Tracker({
        projectKey: 'ukRxx97kV2bv9PE5YEie',
        ingestPoint: `${OPENREPLAY_URL}/ingest`,
        // Embeber el CSS en la sesión en vez de referenciar los <link href> y
        // que el reproductor los re-descargue. Necesario aquí porque el grueso
        // del estilo (tema de antd, ~840KB) lo inyecta react-css-theme-switcher
        // en runtime como <link href="/css/light-theme.css"> root-relativo, que
        // el player no recupera en modo referencia → replays sin CSS.
        // 1 = InlineCssMode.Inline: lee cssRules (same-origin) y, si una hoja es
        // cross-origin, cae automáticamente a fetch del href.
        inlineCss: 1,
        // Habilitar opciones para Co-Browse
        __DISABLE_SECURE_MODE: true, // Permite ver contenido sensible
        // Configuración de captura de red
        network: {
          capturePayload: true, // Captura request/response bodies
          failuresOnly: false, // Captura todas las requests, no solo errores
          ignoreHeaders: [
            'Cookie',
            'Set-Cookie',
            'Authorization',
            'X-Auth-Token',
          ],
          // Sanitizer para proteger datos sensibles
          sanitizer: (data) => {
            // Lista de campos sensibles a sanitizar
            const sensitiveFields = [
              'password',
              'contraseña',
              'token',
              'secret',
              'apiKey',
              'api_key',
              'accessToken',
              'access_token',
              'refreshToken',
              'refresh_token',
              'creditCard',
              'credit_card',
              'cvv',
              'ssn',
              'pin',
            ];

            // Función para sanitizar un objeto recursivamente
            const sanitizeObject = (obj) => {
              if (!obj || typeof obj !== 'object') return obj;

              const sanitized = Array.isArray(obj) ? [...obj] : { ...obj };

              Object.keys(sanitized).forEach((key) => {
                const lowerKey = key.toLowerCase();
                // Verificar si el campo es sensible
                if (
                  sensitiveFields.some((field) =>
                    lowerKey.includes(field.toLowerCase())
                  )
                ) {
                  sanitized[key] = '[REDACTED]';
                } else if (
                  typeof sanitized[key] === 'object' &&
                  sanitized[key] !== null
                ) {
                  sanitized[key] = sanitizeObject(sanitized[key]);
                }
              });

              return sanitized;
            };

            // Sanitizar URLs de autenticación completamente
            const authUrls = ['/auth', '/login', '/signin', '/oauth', '/token'];
            if (authUrls.some((url) => data.url?.toLowerCase().includes(url))) {
              if (data.request?.body) {
                data.request.body = '[REDACTED - Auth Request]';
              }
              if (data.response?.body) {
                data.response.body = '[REDACTED - Auth Response]';
              }
              return data;
            }

            // Sanitizar body del request si existe
            if (data.request?.body) {
              try {
                const parsedBody =
                  typeof data.request.body === 'string'
                    ? JSON.parse(data.request.body)
                    : data.request.body;
                data.request.body = JSON.stringify(sanitizeObject(parsedBody));
              } catch (e) {
                // Si no es JSON, dejarlo como está
              }
            }

            // Sanitizar body del response si existe
            if (data.response?.body) {
              try {
                const parsedBody =
                  typeof data.response.body === 'string'
                    ? JSON.parse(data.response.body)
                    : data.response.body;
                data.response.body = JSON.stringify(sanitizeObject(parsedBody));
              } catch (e) {
                // Si no es JSON, dejarlo como está
              }
            }

            // Sanitizar headers sensibles adicionales
            if (data.request?.headers) {
              const headersToSanitize = ['x-api-key', 'x-auth-token', 'bearer'];
              Object.keys(data.request.headers).forEach((header) => {
                if (
                  headersToSanitize.some((h) =>
                    header.toLowerCase().includes(h)
                  )
                ) {
                  data.request.headers[header] = '[REDACTED]';
                }
              });
            }

            return data;
          },
        },
      });

      // Habilitar el plugin de Assist para Co-Browse y soporte en vivo
      console.log('[OpenReplay] Installing Assist plugin for Co-Browse...');
      try {
        this.tracker.use(
          trackerAssist({
            // Configuración del plugin Assist
            // Esto habilita: Co-browsing, videollamadas, anotaciones, control remoto
          })
        );
        console.log('[OpenReplay] ✅ Assist plugin installed successfully');
      } catch (assistError) {
        console.error('[OpenReplay] ❌ Error installing Assist plugin', {
          error: assistError,
          errorMessage: assistError.message,
        });
        // Continuar aunque falle el plugin de assist
      }

      this.isInitialized = true;
      this.isInitializing = false;

      console.log('[OpenReplay] ✅ Tracker initialized successfully', {
        projectKey: 'ukRxx97kV2bv9PE5YEie',
        ingestPoint: `${OPENREPLAY_URL}/ingest`,
        trackerInstance: !!this.tracker,
        assistEnabled: true,
        networkCapture: {
          capturePayload: true,
          sanitizerEnabled: true,
        },
      });

      return true;
    } catch (error) {
      this.isInitializing = false;
      console.error('[OpenReplay] ❌ Error initializing tracker', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
      });
      return false;
    }
  }

  /**
   * Identifica al usuario en OpenReplay leyendo localStorage('user').
   * @param {boolean} allowAnonymous - Si es true, continúa con sesión anónima si falla.
   */
  identifyUser(allowAnonymous = true) {
    console.log('[OpenReplay] Attempting to identify user...', {
      isProduction: this.isProduction,
      trackerInitialized: !!this.tracker,
      allowAnonymous,
    });

    if (!this.isProduction) {
      console.warn(
        '[OpenReplay] ❌ User identification skipped - Reason: Not in production environment'
      );
      return false;
    }

    if (!this.tracker) {
      console.warn(
        '[OpenReplay] ❌ User identification skipped - Reason: Tracker not initialized'
      );
      return false;
    }

    if (!this.validateDependencies()) {
      console.error(
        '[OpenReplay] ❌ Cannot identify user - Dependencies validation failed'
      );
      if (allowAnonymous) {
        console.log('[OpenReplay] 🔄 Falling back to anonymous session...');
        return true; // Continuar con sesión anónima
      }
      return false;
    }

    try {
      const userData = this.getStoredUser();

      // Extraer información del usuario (excluyendo campos sensibles del metadata)
      const {
        token,
        refreshToken,
        password,
        email,
        username,
        name,
        id,
        _id,
        ...restData
      } = userData;

      // El userId es el email, username o id del usuario
      const userId = email || username || id || _id;

      if (!userId) {
        console.warn(
          '[OpenReplay] ❌ No user identifier available for identification',
          {
            hasUserData: !!userData,
          }
        );
        if (allowAnonymous) {
          console.log('[OpenReplay] 🔄 Continuing with anonymous session...');
          return true;
        }
        return false;
      }

      // Construir metadata excluyendo campos sensibles (token, password, etc.)
      const metadata = {
        id: id || _id,
        name,
        username,
        ...restData,
      };

      // Limpiar valores undefined/null y objetos anidados de metadata
      // (OpenReplay.setMetadata solo acepta valores escalares)
      Object.keys(metadata).forEach((key) => {
        const value = metadata[key];
        if (
          value === undefined ||
          value === null ||
          typeof value === 'object'
        ) {
          delete metadata[key];
        }
      });

      console.log('[OpenReplay] Setting user ID...', { userId });

      try {
        this.tracker.setUserID(String(userId));
        console.log('[OpenReplay] ✅ User ID set successfully', { userId });
      } catch (error) {
        console.error('[OpenReplay] ❌ Error setting user ID', {
          userId,
          error,
          errorMessage: error.message,
        });
        // Continuar aunque falle setUserID
      }

      console.log('[OpenReplay] Setting user metadata...', {
        metadataKeys: Object.keys(metadata),
        metadataCount: Object.keys(metadata).length,
      });

      let metadataSetCount = 0;
      let metadataErrorCount = 0;

      Object.entries(metadata).forEach(([key, value]) => {
        try {
          this.tracker.setMetadata(key, String(value));
          metadataSetCount++;
        } catch (error) {
          metadataErrorCount++;
          console.error('[OpenReplay] ❌ Error setting metadata entry', {
            key,
            value,
            error: error.message,
          });
        }
      });

      console.log('[OpenReplay] ✅ User identified successfully', {
        userId,
        metadataSet: metadataSetCount,
        metadataErrors: metadataErrorCount,
        metadata,
      });

      return true;
    } catch (error) {
      console.error('[OpenReplay] ❌ Error identifying user', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
      });

      if (allowAnonymous) {
        console.log(
          '[OpenReplay] 🔄 Falling back to anonymous session due to error...'
        );
        return true;
      }

      return false;
    }
  }

  /**
   * Verifica que el tracker esté listo para iniciar
   */
  healthCheck() {
    console.log('[OpenReplay] Running health check...');

    const checks = {
      isProduction: this.isProduction,
      trackerExists: !!this.tracker,
      isInitialized: this.isInitialized,
      notAlreadyStarted: !this.isStarted,
    };

    const allPassed = Object.values(checks).every((check) => check === true);

    console.log('[OpenReplay] Health check result:', {
      ...checks,
      passed: allPassed,
    });

    return allPassed;
  }

  /**
   * Obtiene el session ID del tracker
   */
  getSessionID() {
    if (!this.tracker) {
      return null;
    }

    try {
      // El tracker de OpenReplay expone el sessionID a través de getSessionID()
      const sessionID = this.tracker.getSessionID();
      return sessionID;
    } catch (error) {
      console.error('[OpenReplay] ❌ Error getting session ID', error);
      return null;
    }
  }

  /**
   * Obtiene la URL de la sesión para visualizarla
   */
  getSessionURL() {
    const sessionID = this.getSessionID();
    if (!sessionID) {
      return null;
    }

    // URL base de OpenReplay para sesiones
    return `${OPENREPLAY_URL}/session/${sessionID}`;
  }

  /**
   * Inicia la grabación de la sesión
   */
  start() {
    console.log('[OpenReplay] Attempting to start session...', {
      isProduction: this.isProduction,
      trackerInitialized: !!this.tracker,
      isStarted: this.isStarted,
    });

    if (this.isStarted) {
      console.warn('[OpenReplay] ⚠️ Session already started, skipping...');
      return true;
    }

    if (!this.isProduction) {
      console.warn(
        '[OpenReplay] ❌ Session start skipped - Reason: Not in production environment'
      );
      return false;
    }

    if (!this.tracker) {
      console.warn(
        '[OpenReplay] ❌ Session start skipped - Reason: Tracker not initialized'
      );
      return false;
    }

    // Health check antes de iniciar
    if (!this.healthCheck()) {
      console.error(
        '[OpenReplay] ❌ Health check failed, cannot start session'
      );
      return false;
    }

    try {
      // Iniciar la grabación de la sesión
      this.tracker.start();
      this.isStarted = true;

      // Obtener y loguear el session ID
      // Dar un pequeño delay para que el tracker genere el sessionID
      setTimeout(() => {
        this.sessionID = this.getSessionID();
        const sessionURL = this.getSessionURL();

        console.log('[OpenReplay] ✅ Session started successfully');
        console.log('[OpenReplay] 🎥 SESSION ID:', this.sessionID);
        console.log('[OpenReplay] 🔗 Session URL:', sessionURL);
        console.log('[OpenReplay] 📊 View session at:', sessionURL);
      }, 500);

      return true;
    } catch (error) {
      console.error('[OpenReplay] ❌ Error starting session', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
      });
      return false;
    }
  }

  /**
   * Reintenta la inicialización con delays exponenciales
   */
  async retryInit(attempt = 1) {
    if (attempt > this.maxRetries) {
      console.error(
        '[OpenReplay] ❌ Max retries reached, giving up initialization'
      );
      return false;
    }

    const delay = this.retryDelay * attempt;
    console.log(
      `[OpenReplay] 🔄 Retry attempt ${attempt}/${this.maxRetries} in ${delay}ms...`
    );

    await new Promise((resolve) => setTimeout(resolve, delay));

    const success = this.init();
    if (!success) {
      return this.retryInit(attempt + 1);
    }

    return true;
  }

  /**
   * Inicializa y arranca OpenReplay con reintentos y fallbacks
   */
  async initAndStart() {
    console.log('[OpenReplay] ========================================');
    console.log('[OpenReplay] Starting full initialization flow...');
    console.log('[OpenReplay] ========================================');

    // Prevenir múltiples llamadas simultáneas
    if (this.isInitializing) {
      console.warn(
        '[OpenReplay] ⚠️ Initialization already in progress, aborting...'
      );
      return false;
    }

    if (this.isStarted) {
      console.warn('[OpenReplay] ⚠️ Session already started, aborting...');
      return false;
    }

    // Inicializar el tracker
    let initSuccess = this.init();

    // Si falla la inicialización, reintentar
    if (!initSuccess && this.isProduction) {
      console.warn(
        '[OpenReplay] ⚠️ Initial initialization failed, attempting retries...'
      );
      initSuccess = await this.retryInit();
    }

    // Si el tracker se inicializó correctamente
    if (this.tracker && initSuccess) {
      console.log(
        '[OpenReplay] Tracker initialized, proceeding with user identification...'
      );

      // Identificar usuario (permite sesiones anónimas como fallback)
      const identifySuccess = this.identifyUser(true);

      if (!identifySuccess) {
        console.warn(
          '[OpenReplay] ⚠️ User identification failed and anonymous mode disabled'
        );
      }

      // Iniciar sesión (incluso si la identificación falló, sesión anónima)
      const startSuccess = this.start();

      if (startSuccess) {
        console.log('[OpenReplay] ========================================');
        console.log('[OpenReplay] ✅ Full initialization completed');
        console.log('[OpenReplay] ========================================');
        return true;
      }

      console.error('[OpenReplay] ========================================');
      console.error('[OpenReplay] ❌ Session start failed');
      console.error('[OpenReplay] ========================================');
      return false;
    }

    console.warn('[OpenReplay] ========================================');
    console.warn(
      '[OpenReplay] ❌ Initialization flow stopped - Tracker not initialized'
    );
    console.warn('[OpenReplay] ========================================');
    return false;
  }

  /**
   * Detiene la grabación de la sesión
   */
  stop() {
    console.log('[OpenReplay] Attempting to stop session...', {
      isProduction: this.isProduction,
      trackerInitialized: !!this.tracker,
      isStarted: this.isStarted,
      sessionID: this.sessionID,
    });

    if (!this.isProduction) {
      console.warn(
        '[OpenReplay] Session stop skipped - Reason: Not in production environment'
      );
      return;
    }

    if (!this.tracker) {
      console.warn(
        '[OpenReplay] Session stop skipped - Reason: Tracker not initialized'
      );
      return;
    }

    if (!this.isStarted) {
      console.warn(
        '[OpenReplay] Session stop skipped - Reason: Session not started'
      );
      return;
    }

    try {
      this.tracker.stop();
      this.isStarted = false;
      console.log('[OpenReplay] ✅ Session stopped successfully', {
        sessionID: this.sessionID,
      });

      // Reset session ID
      this.sessionID = null;
    } catch (error) {
      console.error('[OpenReplay] ❌ Error stopping session', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
      });
    }
  }

  // Métodos adicionales útiles
  setMetadata(key, value) {
    console.log('[OpenReplay] Setting custom metadata...', { key, value });

    if (!this.isProduction) {
      console.warn(
        '[OpenReplay] ❌ Metadata not set - Reason: Not in production environment'
      );
      return;
    }

    if (!this.tracker) {
      console.warn(
        '[OpenReplay] ❌ Metadata not set - Reason: Tracker not initialized'
      );
      return;
    }

    try {
      this.tracker.setMetadata(key, String(value));
      console.log('[OpenReplay] ✅ Metadata set successfully', {
        key,
        value: String(value),
      });
    } catch (error) {
      console.error('[OpenReplay] ❌ Error setting metadata', {
        key,
        value,
        error,
        errorMessage: error.message,
      });
    }
  }

  event(name, payload = {}) {
    console.log('[OpenReplay] Tracking custom event...', { name, payload });

    if (!this.isProduction) {
      console.warn(
        '[OpenReplay] ❌ Event not tracked - Reason: Not in production environment'
      );
      return;
    }

    if (!this.tracker) {
      console.warn(
        '[OpenReplay] ❌ Event not tracked - Reason: Tracker not initialized'
      );
      return;
    }

    try {
      this.tracker.event(name, payload);
      console.log('[OpenReplay] ✅ Event tracked successfully', {
        name,
        payload,
      });
    } catch (error) {
      console.error('[OpenReplay] ❌ Error tracking event', {
        name,
        payload,
        error,
        errorMessage: error.message,
      });
    }
  }
}

const instance = new OpenReplayService();

export default instance;
